import { useState } from 'react';
import { Button } from './ui/button';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription,
  DialogFooter 
} from './ui/dialog';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { 
  Rocket, Loader2, X, CheckCircle, Target, Users, 
  MessageSquare, DollarSign, Calendar, Zap, ArrowRight,
  AlertTriangle, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE_URL as API } from '../utils/apiConfig';

// Format helpers
const formatNumber = (num) => {
  if (num === null || num === undefined) return '—';
  if (Math.abs(num) >= 10000000) return `${(num / 10000000).toFixed(2)} Cr`;
  if (Math.abs(num) >= 100000) return `${(num / 100000).toFixed(2)} L`;
  if (Math.abs(num) >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('en-IN');
};

const formatCurrency = (num) => `₹${formatNumber(num)}`;

/**
 * LaunchCampaignButton - Universal campaign activation button
 * 
 * Can be used on:
 * - Cohort cards
 * - RFM segment cards
 * - Insight/Recommendation cards
 * - Brand AI Insights recommendations
 * - Store performance segments
 * 
 * Usage:
 * <LaunchCampaignButton
 *   segment={{ name: "One-Timers", customers: 15600, criteria: {...} }}
 *   recommendation={{ action: "Convert...", priority: "P0", impact: "₹45Cr" }}
 *   brandId="30001"
 *   campaignType="cohort"
 *   onCampaignCreated={(campaign) => console.log('Created:', campaign)}
 * />
 */
export function LaunchCampaignButton({
  // Segment/Cohort Info
  segment,
  cohortName,
  segmentName,
  customers,
  criteria,
  
  // Recommendation Info (optional)
  recommendation,
  insight,
  
  // Campaign Config
  brandId,
  campaignType = 'cohort',
  channel = 'sms',
  suggestedMessage,
  expectedROI,
  
  // Callbacks
  onCampaignCreated,
  
  // Styling
  size = 'sm',
  variant = 'default',
  className = '',
  buttonText = 'Launch Campaign',
  showIcon = true
}) {
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [campaignCreated, setCampaignCreated] = useState(null);
  
  // Derive segment info
  const segmentInfo = segment || {
    name: segmentName || cohortName || 'Custom Segment',
    customers: customers || 0,
    criteria: criteria || {}
  };
  
  const handleLaunchCampaign = async () => {
    setLoading(true);
    
    try {
      // Create campaign draft via API
      const response = await axios.post(
        `${API}/api/campaigns/create-from-segment`,
        {
          brand_id: brandId,
          segment: segmentInfo,
          recommendation: recommendation,
          insight: insight,
          campaign_type: campaignType,
          channel: channel,
          suggested_message: suggestedMessage,
          expected_roi: expectedROI
        },
        { withCredentials: true }
      );
      
      if (response.data.success) {
        setCampaignCreated(response.data.campaign);
        toast.success('Campaign draft created!');
        onCampaignCreated?.(response.data.campaign);
      } else {
        toast.error(response.data.error || 'Failed to create campaign');
      }
    } catch (err) {
      console.error('Campaign creation error:', err);
      toast.error('Failed to create campaign');
    } finally {
      setLoading(false);
    }
  };
  
  // Quick action - create campaign immediately
  const handleQuickLaunch = async () => {
    setShowModal(true);
  };
  
  // Recommendation priority styling
  const priorityStyles = {
    P0: 'bg-red-500 text-white',
    P1: 'bg-amber-500 text-white',
    P2: 'bg-blue-500 text-white'
  };
  
  return (
    <>
      <Button
        size={size}
        variant={variant}
        className={`gap-1.5 ${className}`}
        onClick={handleQuickLaunch}
        data-testid="launch-campaign-btn"
      >
        {showIcon && <Rocket className="w-3.5 h-3.5" />}
        {buttonText}
      </Button>
      
      {/* Campaign Confirmation Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-violet-500/20">
                <Rocket className="w-5 h-5 text-violet-500" />
              </div>
              Launch Campaign
            </DialogTitle>
            <DialogDescription>
              Create a targeted campaign for this segment
            </DialogDescription>
          </DialogHeader>
          
          {!campaignCreated ? (
            <div className="space-y-4 py-4">
              {/* Segment Info */}
              <Card className="bg-muted/50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <Target className="w-5 h-5 text-violet-500" />
                    <div>
                      <h4 className="font-semibold">{segmentInfo.name}</h4>
                      <p className="text-xs text-muted-foreground">Target Segment</p>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-2 rounded-lg bg-background/50">
                      <div className="flex items-center gap-2 mb-1">
                        <Users className="w-4 h-4 text-blue-500" />
                        <span className="text-xs text-muted-foreground">Audience</span>
                      </div>
                      <p className="text-lg font-bold">{formatNumber(segmentInfo.customers)}</p>
                    </div>
                    <div className="p-2 rounded-lg bg-background/50">
                      <div className="flex items-center gap-2 mb-1">
                        <MessageSquare className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-muted-foreground">Channel</span>
                      </div>
                      <p className="text-lg font-bold uppercase">{channel}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Recommendation (if present) */}
              {recommendation && (
                <Card className="border-violet-500/30 bg-gradient-to-br from-violet-500/5 to-purple-500/5">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-medium text-violet-400">AI Recommendation</span>
                          {recommendation.priority && (
                            <Badge className={`text-xs ${priorityStyles[recommendation.priority] || priorityStyles.P1}`}>
                              {recommendation.priority}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm">{recommendation.action || recommendation.text}</p>
                        {recommendation.impact && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <DollarSign className="w-3 h-3" />
                            <span>Expected Impact: {recommendation.impact}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Expected ROI */}
              {expectedROI && (
                <div className="p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Expected ROI</span>
                    <span className="text-lg font-bold text-emerald-400">{expectedROI}</span>
                  </div>
                </div>
              )}
              
              {/* Warning */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
                <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted-foreground">
                  This will create a campaign draft. You can review and modify the message before sending.
                </p>
              </div>
            </div>
          ) : (
            <div className="py-6">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-emerald-500" />
                </div>
                <h3 className="text-lg font-semibold mb-2">Campaign Draft Created!</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  {campaignCreated.name}
                </p>
                
                <div className="grid grid-cols-2 gap-3 text-left mb-4">
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Target Audience</p>
                    <p className="font-semibold">{formatNumber(campaignCreated.estimated_audience_size || segmentInfo.customers)}</p>
                  </div>
                  <div className="p-3 rounded-lg bg-muted/50">
                    <p className="text-xs text-muted-foreground">Channel</p>
                    <p className="font-semibold uppercase">{campaignCreated.channel || channel}</p>
                  </div>
                </div>
                
                <p className="text-xs text-emerald-500">
                  Go to Campaign Builder to review and send
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {!campaignCreated ? (
              <>
                <Button variant="outline" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={handleLaunchCampaign} 
                  disabled={loading}
                  className="gap-2 bg-violet-500 hover:bg-violet-600"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Rocket className="w-4 h-4" />
                  )}
                  {loading ? 'Creating...' : 'Create Campaign Draft'}
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowModal(false)}>
                Done
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * RecommendationActionCard - Card with built-in campaign activation
 * Use this for displaying recommendations with campaign launch capability
 */
export function RecommendationActionCard({
  priority,
  action,
  owner,
  impact,
  segment,
  customers,
  brandId,
  onCampaignCreated
}) {
  const priorityConfig = {
    P0: { label: 'IMMEDIATE', color: 'bg-red-500', textColor: 'text-red-400' },
    P1: { label: 'THIS MONTH', color: 'bg-amber-500', textColor: 'text-amber-400' },
    P2: { label: 'THIS QUARTER', color: 'bg-blue-500', textColor: 'text-blue-400' }
  };
  
  const config = priorityConfig[priority] || priorityConfig.P1;
  
  return (
    <Card className="border-l-4" style={{ borderLeftColor: priority === 'P0' ? '#ef4444' : priority === 'P1' ? '#f59e0b' : '#3b82f6' }}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <Badge className={`${config.color} text-white text-xs`}>
                {priority} - {config.label}
              </Badge>
            </div>
            <p className="text-sm font-medium mb-2">{action}</p>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
              {owner && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Owner: {owner}
                </span>
              )}
              {impact && (
                <span className="flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  Impact: {impact}
                </span>
              )}
              {customers && (
                <span className="flex items-center gap-1">
                  <Target className="w-3 h-3" />
                  Target: {formatNumber(customers)}
                </span>
              )}
            </div>
          </div>
          <LaunchCampaignButton
            segment={{ name: segment, customers: customers }}
            recommendation={{ action, priority, impact }}
            brandId={brandId}
            onCampaignCreated={onCampaignCreated}
            size="sm"
            className="bg-violet-500 hover:bg-violet-600 flex-shrink-0"
          />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * CohortActionBadge - Badge with campaign launch for cohorts
 * Displays the action type and allows launching campaign
 */
export function CohortActionBadge({
  action,
  cohortName,
  customers,
  brandId,
  onCampaignCreated
}) {
  const actionConfig = {
    CONVERT: { color: 'bg-blue-500/20 text-blue-400 border-blue-500/30', roi: '3-5x' },
    NURTURE: { color: 'bg-teal-500/20 text-teal-400 border-teal-500/30', roi: '4-6x' },
    GROW: { color: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30', roi: '5-8x' },
    RETAIN: { color: 'bg-orange-500/20 text-orange-400 border-orange-500/30', roi: '6-10x' },
    REACTIVATE: { color: 'bg-amber-500/20 text-amber-400 border-amber-500/30', roi: '2-4x' },
    SAVE: { color: 'bg-red-500/20 text-red-400 border-red-500/30', roi: '1-3x' },
    IGNORE: { color: 'bg-gray-500/20 text-gray-400 border-gray-500/30', roi: '0.5-1x' }
  };
  
  const config = actionConfig[action] || actionConfig.CONVERT;
  
  return (
    <div className="flex items-center gap-2">
      <Badge className={`${config.color} border cursor-default`}>
        {action}
      </Badge>
      <LaunchCampaignButton
        cohortName={cohortName}
        customers={customers}
        expectedROI={config.roi}
        brandId={brandId}
        onCampaignCreated={onCampaignCreated}
        size="sm"
        variant="ghost"
        className="h-6 px-2 text-xs"
        buttonText=""
        showIcon={true}
      />
    </div>
  );
}

export default LaunchCampaignButton;
